// src/components/erp/security/LoginSigmaKey.jsx
import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './../../style/Vars.css';
import './../../style/ElementsStyle.css';
import './../../style/ContainersStyles.css';
import { isMobile, isTablet } from 'react-device-detect';
import bgLogin from './img/bglogin.png';
import userIcon from './img/usericon.png';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import api, { API_BASE } from '../../api';
import { ENDPOINTS } from '../Seguridad/Utils/Endpoints';
import { ENDPOINTS as GENERAL_ENDPOINTS } from '../General/Utils/Endpoints';

import Header from './Header';
import Footer from './Footer';
import { setSessionUser } from '../../utils/SessionUser';
import { useLogos } from '../../context/LogoProvider';
import AvisosPrivacidadAceptar from '../General/Components/Catalogos/AvisosPrivacidad/AvisosPrivacidadAceptar';

// Helper para URL de imagen (mismo esquema que en modales)
// Helper para URL de imagen (mismo esquema que en modales)
// const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');

export default function LoginSigmaKey() {
  const navigate = useNavigate();

  const [nombreUsuario, setNombreUsuario] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [error, setError] = useState('');
  // Nivel de severidad: 0=ninguno, 1=401 (mal password), 2=423 (locked), 3=429 (rate limit)
  // Solo escala hacia arriba — nunca baja entre clicks de la misma sesión.
  const [errorLevel, setErrorLevel] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const { getSystemImageUrl, refreshResources } = useLogos();
  const background = getSystemImageUrl('LOGIN', bgLogin);

  const containerClass = isMobile || isTablet
    ? 'login-form-container login-form-container-mobile'
    : 'login-form-container';
  const cardClass = isMobile || isTablet
    ? 'login-form-card login-form-card-mobile'
    : 'login-form-card';
  const inputClass = isMobile || isTablet
    ? 'login-form-input login-form-input-mobile'
    : 'login-form-input';
  const buttonClass = isMobile || isTablet
    ? 'login-form-button login-form-button-mobile'
    : 'login-form-button';

  const containerStyle = {
    backgroundImage: `url(${background})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  const [avisosPendientes, setAvisosPendientes] = useState([]);
  const [showAvisos, setShowAvisos] = useState(false);
  const [tempSession, setTempSession] = useState(null);

  // --- GOOGLE reCAPTCHA v2 (reactivo) ---
  const RECAPTCHA_SITE_KEY = '6LeowvwsAAAAAEJG2D_WG2N4IhwlkXu3Zo0BwpkV';
  const recaptchaRef = useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [requiereCaptcha, setRequiereCaptcha] = useState(false);

  useEffect(() => {
    checkCaptchaStatus();
  }, []);

  const checkCaptchaStatus = async () => {
    try {
      const response = await api.get('Captcha/check');
      if (response.data.requiereCaptcha) {
        setRequiereCaptcha(true);
      }
    } catch (error) {
      console.warn('[Captcha] No se pudo verificar el estado automático', error);
    }
  };

  const handleLogin = async () => {
    // 1. VERIFICACIÓN DE ÚLTIMO SEGUNDO: ¿Se activó el captcha mientras el usuario escribía?
    const checkRes = await api.get('Captcha/check');
    if (checkRes.data.requiereCaptcha && !requiereCaptcha) {
      setRequiereCaptcha(true);
      setError('Se ha activado una medida de seguridad por tráfico inusual. Por favor, completa el reCAPTCHA.');
      setErrorLevel(2);
      return;
    }

    // 2. VALIDAR token si el reCAPTCHA está activo
    if (requiereCaptcha && !recaptchaToken) {
      setError('Por favor, completa el reCAPTCHA antes de continuar.');
      setErrorLevel(2);
      return;
    }

    try {
      const response = await api.post(ENDPOINTS.usuarios + '/validar', {
        nombreUsuario: nombreUsuario,
        contrasenaHASH: contrasena
      });

      const data = response.data;
      
      // La API ahora ya no devuelve Token en el body, sino vía cookie HttpOnly
      if (data && data.IdUsuario) {
        const idUsuario = data.IdUsuario;
        const nombreUsuarioResp = data.NombreUsuario;

        // 1. Obtener el timeout dinámico
        let minutosTimeout = 15;
        try {
          const timeoutRes = await api.get(ENDPOINTS.configuracionTimeout(idUsuario || 0));
          minutosTimeout = parseFloat(timeoutRes.data);
        } catch (error) {
          console.warn('[Login] No se pudo obtener el timeout dinámico', error);
        }

        const sessionData = {
          idUsuario: idUsuario,
          nombreUsuario: nombreUsuarioResp,
          idEmpresa: data.IdEmpresa ?? data.idEmpresa ?? null,
          idPerfil: data.IdPerfil ?? data.idPerfil ?? null,
          minutosInactividad: minutosTimeout,
          xsrfToken: data.XsrfToken ?? data.xsrfToken ?? null
        };

        // 2. Verificar Avisos de Privacidad PENDIENTES
        try {
          const resAvisos = await api.get(GENERAL_ENDPOINTS.avisosPrivacidadPendientes(idUsuario));
          const list = resAvisos.data || [];
          if (list.length > 0) {
            setAvisosPendientes(list);
            setTempSession(sessionData);
            setShowAvisos(true);
            return;
          }
        } catch (error) {
          console.warn('[Login] Error al verificar avisos pendientes', error);
        }

        // Login exitoso: resetear todo
        setErrorLevel(0);
        setError('');
        setRecaptchaToken('');
        recaptchaRef.current?.reset();
        finalizarLogin(sessionData);
      } else {
        // Fallback: Si no hay IdUsuario pero sí 200, algo salió mal
        if (errorLevel < 1) setErrorLevel(1);
        setError('Error al validar sesión.');
      }
    } catch (err) {
      console.error(err);
      const status = err.response?.status;
      const mensaje = err.response?.data?.message;

      // Resetear reCAPTCHA tras cualquier error para forzar re-verificación
      setRecaptchaToken('');
      recaptchaRef.current?.reset();

      if (status === 429) {
        // Nivel 3 — máxima prioridad, SIEMPRE actualiza el mensaje
        setErrorLevel(3);
        setError(mensaje || 'Demasiadas solicitudes. Espera un momento e intenta de nuevo.');
      } else if (status === 423) {
        // Nivel 2 — SIEMPRE actualiza el mensaje (el level solo controla el estilo)
        setErrorLevel(prev => Math.max(prev, 2));
        setError(mensaje || 'Tu cuenta está bloqueada temporalmente. Intenta más tarde.');
      } else if (status === 401) {
        // Nivel 1 — SIEMPRE muestra el mensaje, pero el estilo no baja si ya era nivel 2+
        if (errorLevel < 2) setErrorLevel(1);
        setError(mensaje || 'Usuario o contraseña incorrectos.');
      } else {
        setError('Error al conectar con el servidor.');
      }
    }
  };

  const finalizarLogin = async (sessionData) => {
    setSessionUser(sessionData);
    // Actualizar recursos visuales para la empresa del usuario
    if (refreshResources) {
      await refreshResources();
    }
    navigate('/menu');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="page-container">
      {showAvisos && (
        <AvisosPrivacidadAceptar
          idUsuario={tempSession.idUsuario}
          avisos={avisosPendientes}
          onFinished={() => finalizarLogin(tempSession)}
        />
      )}
      <Header />

      <main className="page-main">
        <div className={containerClass} style={containerStyle}>
          <div className={cardClass} role="form" aria-label="Formulario de inicio de sesión">
            <img src={userIcon} alt="Icono de usuario" className="login-form-icon-img" />
            <h2 className="login-form-title">Iniciar Sesión</h2>

            <input
              type="text"
              placeholder="Nombre de Usuario"
              className={inputClass}
              value={nombreUsuario}
              onChange={(e) => setNombreUsuario(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Nombre de Usuario"
              autoComplete="username"
            />
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña"
                className={inputClass}
                value={contrasena}
                onChange={(e) => setContrasena(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Contraseña"
                autoComplete="current-password"
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                  zIndex: 10,
                }}
                title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>

            <div className="login-form-options" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="login-form-forgot"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', color: 'inherit' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
              >
                Olvidé mi Contraseña
              </button>
            </div>

            {/* Google reCAPTCHA v2 — solo aparece cuando el backend lo solicita */}
            {requiereCaptcha && (
              <div style={{
                width: '100%',
                padding: '12px',
                marginBottom: '15px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <label style={{ color: '#fff', fontSize: '13px', textAlign: 'center', opacity: 0.8 }}>
                  Seguridad reactiva activada — verifica que no eres un robot
                </label>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  theme="dark"
                  onChange={(token) => setRecaptchaToken(token || '')}
                  onExpired={() => setRecaptchaToken('')}
                  onError={() => setRecaptchaToken('')}
                />
              </div>
            )}

            <button
              className={buttonClass}
              onClick={handleLogin}
              disabled={requiereCaptcha && !recaptchaToken}
              style={{
                opacity: (requiereCaptcha && !recaptchaToken) ? 0.55 : 1,
                cursor: (requiereCaptcha && !recaptchaToken) ? 'not-allowed' : 'pointer'
              }}
            >
              <FaLock />
              <span> Iniciar Sesión</span>
            </button>

            <div style={{
              marginTop: '1rem',
              padding: '0.65rem 0.9rem',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              textAlign: 'center',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              transition: 'opacity 0.25s ease',
              opacity: error ? 1 : 0,
              pointerEvents: error ? 'auto' : 'none',
              minHeight: '2.4rem',
              // Estilo según nivel: 1=rojo, 2=ámbar, 3=naranja
              ...(errorLevel === 3 ? {
                background: 'rgba(255, 140, 0, 0.2)',
                border: '1px solid rgba(255, 140, 0, 0.6)',
                color: '#ffb74d',
                boxShadow: '0 2px 12px rgba(255, 140, 0, 0.18)',
              } : errorLevel === 2 ? {
                background: 'rgba(255, 200, 60, 0.18)',
                border: '1px solid rgba(255, 200, 60, 0.55)',
                color: '#ffe082',
                boxShadow: '0 2px 12px rgba(255, 180, 0, 0.15)',
              } : {
                background: 'rgba(255, 80, 80, 0.15)',
                border: '1px solid rgba(255, 100, 100, 0.4)',
                color: '#ffcdd2',
                boxShadow: '0 2px 12px rgba(255, 80, 80, 0.12)',
              })
            }}>
              <span style={{ fontSize: '1rem', flexShrink: 0, lineHeight: 1.4 }}>
                {errorLevel === 3 ? '🚫' : errorLevel === 2 ? '🔒' : '⚠️'}
              </span>
              <span style={{ lineHeight: 1.45, fontWeight: 500 }}>{error}</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
